import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { fetchCompetitionGames } from '../services/competitionApi';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { FinishedGameCard } from '../components/dashboard/FinishedGameCard';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import toast from 'react-hot-toast';
import { Award, Eye, Trophy } from 'lucide-react';

const CompletedGames = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { competitionSlug, basePath } = useCompetition();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchCompetitionGames(competitionSlug, 'finished');
        if (!cancelled) setGames(data);
      } catch (error) {
        console.error('Error fetching completed games:', error);
        toast.error(
          language === 'en' ? 'Failed to load completed games' : 'נכשל בטעינת משחקים שהסתיימו'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionSlug, language]);

  if (loading) {
    return (
      <PageLoading
        message={language === 'en' ? 'Loading completed games…' : 'טוען משחקים שהסתיימו…'}
      />
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={language === 'en' ? 'Completed games' : 'משחקים שהסתיימו'}
        subtitle={
          language === 'en'
            ? `${games.length} completed games with results`
            : `${games.length} משחקים שהסתיימו עם תוצאות`
        }
        icon={Trophy}
        actions={
          <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
            <Award className="h-4 w-4" />
            {language === 'en' ? 'Results & points' : 'תוצאות ונקודות'}
          </span>
        }
      />

      {games.length === 0 ? (
        <Card className="py-16 text-center">
          <Trophy className="mx-auto mb-4 h-12 w-12 text-zinc-300" />
          <p className="text-lg font-semibold text-zinc-800">
            {language === 'en' ? 'No completed games yet' : 'אין משחקים שהסתיימו עדיין'}
          </p>
          <p className="mt-2 text-sm text-zinc-500">
            {language === 'en'
              ? 'Finished matches will appear here with results.'
              : 'משחקים שהסתיימו יופיעו כאן עם התוצאות.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {games.map((game) => (
            <div key={game._id} className="space-y-2">
              <FinishedGameCard game={game} userBet={game.userBet} />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`${basePath}/games/${game._id}/bets`)}
                >
                  <Eye className="h-4 w-4" />
                  {language === 'en' ? 'All user bets' : 'כל הימורי המשתמשים'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};

export default CompletedGames;
